import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Calendar, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Reveal, Chapter, Hairline } from "./Reveal";
import { WHATSAPP_URL, WHATSAPP_DISPLAY, EMAIL } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "In-Hand & Groundwork Training",
  "Under-Saddle Horse Training",
  "Horse & Rider Coaching",
  "Vacation Care & Exercise",
  "Not sure yet — advise me",
];

const inputCls =
  "w-full border-b border-champagne/30 bg-transparent py-3 text-sm font-light text-cream placeholder:text-cream/30 outline-none transition-colors duration-300 focus:border-gold";

const Contact = () => {
  const [form, setForm] = useState({
    owner_name: "",
    email: "",
    whatsapp: "",
    horse_name_breed: "",
    service: "",
    preferred_dates: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please choose a desired service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, form);
      toast.success("Request received — Kiara will be in touch shortly.");
      setForm({ owner_name: "", email: "", whatsapp: "", horse_name_breed: "", service: "", preferred_dates: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="consultation" data-testid="consultation-section" className="bg-charcoal py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <Chapter number="05" title="The Consultation" testId="consultation-chapter" />
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-8 font-serif text-4xl font-light leading-tight tracking-tight text-offwhite md:text-5xl">
                Begin the{" "}
                <em className="italic text-gold">conversation.</em>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-md text-base font-light leading-relaxed text-cream/60">
                Tell Kiara about your horse and your goals for the season.
                Every enquiry receives a personal reply — usually within a day.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-12 space-y-0">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-contact-link"
                  className="group flex items-center gap-5 border-t border-champagne/15 py-6 transition-colors duration-300"
                >
                  <MessageCircle size={20} strokeWidth={1.5} className="text-gold" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-cream/40">WhatsApp</p>
                    <p className="mt-1 font-serif text-xl text-cream transition-colors duration-300 group-hover:text-gold">
                      {WHATSAPP_DISPLAY}
                    </p>
                  </div>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="email-contact-link"
                  className="group flex items-center gap-5 border-t border-champagne/15 py-6"
                >
                  <Mail size={20} strokeWidth={1.5} className="text-gold" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-cream/40">Email</p>
                    <p className="mt-1 font-serif text-xl text-cream transition-colors duration-300 group-hover:text-gold">
                      {EMAIL}
                    </p>
                  </div>
                </a>
                <div className="flex items-center gap-5 border-t border-champagne/15 py-6" data-testid="location-row">
                  <MapPin size={20} strokeWidth={1.5} className="text-gold" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-cream/40">Service Area</p>
                    <p className="mt-1 font-serif text-xl text-cream">
                      Your stables · Anywhere in Dubai
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-5 border-y border-champagne/15 py-6" data-testid="season-row">
                  <Calendar size={20} strokeWidth={1.5} className="text-gold" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-cream/40">Season</p>
                    <p className="mt-1 font-serif text-xl text-cream">October — May · Winter only</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <form
              onSubmit={onSubmit}
              data-testid="consultation-form"
              className="border border-champagne/20 bg-ebony/70 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)] backdrop-blur-2xl md:p-12"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-champagne">
                Consultation Intake
              </p>
              <Hairline className="mt-6" />

              <div className="mt-10 space-y-9">
                <div>
                  <label htmlFor="owner-name" className="text-[10px] uppercase tracking-[0.3em] text-cream/45">
                    Owner Name
                  </label>
                  <input
                    id="owner-name"
                    data-testid="input-owner-name"
                    required
                    value={form.owner_name}
                    onChange={set("owner_name")}
                    placeholder="Your full name"
                    className={inputCls}
                  />
                </div>
                <div className="grid gap-9 sm:grid-cols-2">
                  <div>
                    <label htmlFor="email" className="text-[10px] uppercase tracking-[0.3em] text-cream/45">
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      data-testid="input-email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.com"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="whatsapp" className="text-[10px] uppercase tracking-[0.3em] text-cream/45">
                      WhatsApp Number <span className="normal-case tracking-normal text-cream/30">(optional)</span>
                    </label>
                    <input
                      id="whatsapp"
                      data-testid="input-whatsapp"
                      value={form.whatsapp}
                      onChange={set("whatsapp")}
                      placeholder="+971 5X XXX XXXX"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="horse-name-breed" className="text-[10px] uppercase tracking-[0.3em] text-cream/45">
                    Horse Name / Breed
                  </label>
                  <input
                    id="horse-name-breed"
                    data-testid="input-horse-name-breed"
                    required
                    value={form.horse_name_breed}
                    onChange={set("horse_name_breed")}
                    placeholder="e.g. Amira — Purebred Arabian, 6yo mare"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] uppercase tracking-[0.3em] text-cream/45">
                    Desired Service
                  </label>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm({ ...form, service: v })}
                  >
                    <SelectTrigger
                      data-testid="select-service"
                      className="h-auto rounded-none border-0 border-b border-champagne/30 bg-transparent px-0 py-3 text-sm font-light text-cream shadow-none focus:ring-0 data-[placeholder]:text-cream/30"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-champagne/30 bg-charcoal text-cream">
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={`service-option-${s.slice(0, 8).toLowerCase().replace(/[^a-z]/g, "-")}`}
                          className="text-sm font-light focus:bg-gold/10 focus:text-cream"
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label htmlFor="preferred-dates" className="text-[10px] uppercase tracking-[0.3em] text-cream/45">
                    Preferred Booking Dates
                  </label>
                  <input
                    id="preferred-dates"
                    data-testid="input-preferred-dates"
                    required
                    value={form.preferred_dates}
                    onChange={set("preferred_dates")}
                    placeholder="e.g. Mid-November, weekday mornings"
                    className={inputCls}
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit"
                disabled={loading}
                className="mt-12 flex w-full items-center justify-center gap-3 border border-gold bg-gold py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ebony transition-colors duration-500 hover:bg-transparent hover:text-gold disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending…" : "Request Consultation"}
              </button>
              <p className="mt-6 text-center text-[11px] font-light tracking-wide text-cream/35">
                Exclusively for female horse owners · October — May
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
